/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Multi RemediationComponent
 *
 * @file MultiRemediationComponent.jsx
 * @author Mohamed yasar arabath M
 */

import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ChannelsVideoRecommendationCard from '../common/ChannelsVideoComponent/ChannelsVideoCard';
import ChannelsPracticeRecommendationCard from '../common/ChannelsPracticeCard/ChannelsPracticeCard';
import useMultiRemediationComponentStyles from './styles/MultiRemediationComponentStyles';
import Carousel from '../carousel/Carousel';
import Utils from '../../common/utils';

const MultiRemediationComponent = (props) => {
  const {
    assets,
    type,
    channelId,
    env,
    getUserCallback,
    onSeeInChannelsLinkClick,
    containerProps,
    onInit = () => {}
  } = props;
  const carouselContainerRef = useRef(null);
  const carouselRef = useRef(null);

  useEffect(() => {
    onInit();
  }, []);

  useEffect(() => {
    const containerRef = carouselContainerRef.current;
    if (!containerRef) return;

    const updateWidthVar = (width) => {
      const value = Utils.getItemWidth(width);

      const root = document.documentElement;
      const current = root.style.getPropertyValue('--assetsContainer-width');
      if (current === value) return;
      root.style.setProperty('--assetsContainer-width', value);
    };

    updateWidthVar(containerRef.offsetWidth);

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];

      requestAnimationFrame(() => {
        updateWidthVar(entry.contentRect.width);
      });
    });

    resizeObserver.observe(containerRef);

    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver.unobserve(containerRef);
    };
  }, []);

  const classes = useMultiRemediationComponentStyles(props);

  const isReadyToShow = () => assets && assets.length > 0
    && type && type.video
    && type.practice
    && type.video.length > 0
    && type.practice.length > 0;

  const renderVideoAsset = (asset) => {
    if (!type.video.includes(asset.type)) return null;

    return (
      <ChannelsVideoRecommendationCard
        asset={asset}
        env={env}
        user={getUserCallback()}
        channelId={channelId}
        onSeeInChannelsLinkClick={onSeeInChannelsLinkClick}
      />
    );
  };

  const renderPracticeAsset = (asset) => {
    if (!type.practice.includes(asset.type)) return null;

    return (
      <ChannelsPracticeRecommendationCard
        asset={asset}
        channelId={channelId}
        env={env}
        user={getUserCallback()}
        onSeeInChannelsLinkClick={onSeeInChannelsLinkClick}
      />
    );
  };

  const renderAssets = (asset) => {
    if (!type.video.includes(asset.type) && !type.practice.includes(asset.type)) {
      return null;
    }

    return (
      <div className={classes.assetsContainer}>
        {renderVideoAsset(asset)}
        {renderPracticeAsset(asset)}
      </div>
    );
  };

  return isReadyToShow() ? (
    <div
      ref={carouselContainerRef}
      className={`${classes.container} ${classes.carouselContainer}`}
      {...containerProps}
    >
      <Carousel
        ref={carouselRef}
        enableSlider
        enableArrowNav
        dots
        onNavigation={() => {}}
      >
        {assets.map((asset, index) => (
          <Fragment key={asset?.id || `asset-${index}`}>
            {renderAssets(asset)}
          </Fragment>
        ))}
      </Carousel>
    </div>
  ) : null;
};

MultiRemediationComponent.propTypes = {
  assets: PropTypes.arrayOf(PropTypes.object),
  type: PropTypes.object,
  channelId: PropTypes.string.isRequired,
  env: PropTypes.string,
  getUserCallback: PropTypes.func,
  onSeeInChannelsLinkClick: PropTypes.func,
  containerProps: PropTypes.object,
  gridContainerProps: PropTypes.object,
  onInit: PropTypes.func
};

MultiRemediationComponent.defaultProps = {
  assets: [],
  type: {
    video: ['video-stream'],
    practice: ['multiple-choice']
  },
  env: 'stg',
  getUserCallback: () => ({ userId: 'demo', token: 'demo' }),
  onSeeInChannelsLinkClick: () => {},
  containerProps: {},
  gridContainerProps: {
    spacing: 16,
    columns: {
      xs: 1, sm: 1, md: 2, mlg: 2, lg: 2, xl: 3, xxl: 3
    }
  },
  onInit: () => {}
};

export default MultiRemediationComponent;
